import ViewerContainer from 'app/containers/ViewerContainer/ViewerContainer';
import { getContentCms } from 'core/crud/contents.crud';
import { useTranslate } from 'core/hooks/useTranslate';
import { getIconByContentType, getTypeNameByContentType } from 'core/utils/lessonItems';
import { isAbsoluteUrl } from 'core/utils/url';
import { successResponse } from 'core/utils/utils';
import { CONTENT_TYPES, CONTENT_TYPES_NAMES } from 'core/variables/constant';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import Button from 'ui/atoms/Button/Button';
import Dialog from 'ui/organisms/dialogs/Dialog/Dialog';
import IconBubble from 'ui/molecules/IconBubble/IconBubble';
import Loading from 'ui/atoms/Loading/Loading';
import TextHtml from 'ui/atoms/TextHtml/TextHtml';
import AttachmentCard from 'core/modules/components/cards/AttachmentCard';

function DialogContentPreview({
  open,
  onClose,
  guid,
  title,
  type,
  description,
  onHandleResourceLibrary,
  buttonLibraryText,
  loadingText,
  resourceId,
  onViewContent,
  showViewContentButton,
}) {
  const { t } = useTranslate();
  const contentType = CONTENT_TYPES_NAMES[type];
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const isContentTypeDocument = contentType === 'PDF' || contentType === 'Office' || contentType === 'Fixed Layout';

  useEffect(() => {
    if (type === CONTENT_TYPES.LINK && guid) {
      setLoading(true);
      getContent();
    }
  }, [type]);

  async function getContent() {
    const response = await getContentCms(guid);
    if (successResponse(response)) {
      setContent(response.data.data);
      setLoading(false);
    } else {
      setLoading(false);
    }
  }

  function onOpenLink(content) {
    const contentUrlAbs = isAbsoluteUrl(content.url) ? content.url : 'https://' + content.url;
    window.open(contentUrlAbs, '_blank');
  }

  function getContentTypeText(contentType) {
    const translatedType = t(`types-resources:${contentType}`);
    const isTranslationValid = translatedType && translatedType !== contentType;
    return isTranslationValid ? translatedType : getTypeNameByContentType(contentType);
  }

  // Va por `customHeader` y no por el prop `title` del Dialog: como `title` metería este
  // bloque dentro del `<h2>` que genera el Dialog (HTML inválido y nombre accesible verboso).
  function componentDialogContentHeader() {
    const typeText = getContentTypeText(type);

    return (
      <div className="dialog-content-preview__header">
        <div className="dialog-content-preview__header-title">
          <div className="dialog-content-preview__header-title-icon">
            <IconBubble name={getIconByContentType(type)} background="alpha" size="lg" color="primary" />
          </div>
          <div className="dialog-content-preview__header-title-text">
            <div className="dialog-content-preview__header-title-text-type">{typeText}</div>
            <h2 className="dialog-content-preview__header-title-text-title">{title}</h2>
            {description && (
              <div className="dialog-content-preview__header-title-text-subtitle">
                <TextHtml text={description} />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
  return (
    <Dialog
      isOpen={open}
      onClose={onClose}
      ariaLabel={title || getContentTypeText(type)}
      customHeader={componentDialogContentHeader()}
      size={isContentTypeDocument ? 'xxl' : 'l'}
      className={`dialog-content-preview ${isContentTypeDocument ? 'dialog-content-preview--pdf' : ''}`}
    >
      {type !== CONTENT_TYPES.LINK && <ViewerContainer config={{ pdfConfig: [], disabledFetchData: true }} contentGuid={guid} showEmbed />}
      {type === CONTENT_TYPES.LINK && content && !loading && (
        <AttachmentCard content={content} guid={guid} type={type} title={title} style="link" onShowVisor={() => onOpenLink(content)} />
      )}
      {type === CONTENT_TYPES.LINK && loading && <Loading />}
      <div className="dialog-content-preview__footer">
        {buttonLibraryText && onHandleResourceLibrary && (
          <div className="dialog-content-preview__footer-actions">
            <Button text={buttonLibraryText} onClick={() => onHandleResourceLibrary(resourceId)} variant="primary-ghost" isLoading={loadingText} isFullWidth />
          </div>
        )}
        {showViewContentButton && onViewContent && type !== CONTENT_TYPES.ZIP && (
          <div className="dialog-content-preview__footer-actions">
            <Button text={t('library:View content')} onClick={onViewContent} variant="primary" isFullWidth />
          </div>
        )}
      </div>
    </Dialog>
  );
}

DialogContentPreview.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  guid: PropTypes.string,
  title: PropTypes.string,
  type: PropTypes.string,
  description: PropTypes.string,
  onHandleResourceLibrary: PropTypes.func,
  buttonLibraryText: PropTypes.string,
  loadingText: PropTypes.bool,
  resourceId: PropTypes.string,
  onViewContent: PropTypes.func,
  showViewContentButton: PropTypes.bool,
};

DialogContentPreview.defaultProps = {
  type: 'CTTY_00',
  showViewContentButton: true,
};

export default DialogContentPreview;
